import { FC, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { useQuery } from "convex/react";
import { api, Doc, Id } from "@/smart-views/client/api";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";

interface SmartViewVersionPanelProps {
  smartViewId: string;
  currentVersionId: Id<"smartViewVersions"> | undefined;
  previewVersionId: Id<"smartViewVersions"> | null;
  onPreview: (versionId: Id<"smartViewVersions"> | null) => void;
  onClose: () => void;
}

export const SmartViewVersionPanel: FC<SmartViewVersionPanelProps> = ({
  smartViewId,
  currentVersionId,
  previewVersionId,
  onPreview,
  onClose,
}) => {
  const versions = useQuery(api.smartViews.client.listVersions, { smartViewId });
  const [showAllVersions, setShowAllVersions] = useState(false);
  const condensedVersions = useMemo(
    () =>
      versions === undefined
        ? []
        : getCondensedVersions({
            versions,
            currentVersionId,
            previewVersionId,
          }),
    [currentVersionId, previewVersionId, versions]
  );
  const visibleVersions = showAllVersions ? (versions ?? []) : condensedVersions;
  const hiddenVersionCount =
    versions === undefined ? 0 : versions.length - condensedVersions.length;

  return (
    <Panel>
      <Header>
        <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
          Version history
        </MDSText>
        <MDSIconButton
          size={MDSIconButtonSize.XSmall}
          iconKind={MDSIconKind.ArrowRightToLine}
          variant={MDSIconButtonVariant.OnChrome}
          onClick={onClose}
          rounded
        />
      </Header>
      {versions !== undefined && hiddenVersionCount > 0 && (
        <CondensedSummary>
          <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
            {showAllVersions
              ? `${versions.length} versions`
              : `${condensedVersions.length} restore points`}
          </MDSText>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onClick={() => setShowAllVersions(value => !value)}
          >
            {showAllVersions ? "Condense" : `Show all ${versions.length}`}
          </MDSButton>
        </CondensedSummary>
      )}
      <List>
        {versions === undefined && (
          <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
            Loading…
          </MDSText>
        )}
        {visibleVersions.map(version => {
          const isCurrent = version._id === currentVersionId;
          const isPreviewing = version._id === previewVersionId;
          return (
            <Row
              key={version._id}
              $active={isPreviewing || (!previewVersionId && isCurrent)}
              onClick={() => onPreview(isCurrent ? null : version._id)}
            >
              <RowLeft>
                <RowTitle>
                  v{version.versionNumber}
                  {isCurrent && <CurrentTag>Current</CurrentTag>}
                </RowTitle>
                <RowSubtitle>{labelForReason(version.reason)}</RowSubtitle>
              </RowLeft>
              <RowMeta>{formatRelative(version.createdAt)}</RowMeta>
            </Row>
          );
        })}
      </List>
    </Panel>
  );
};

const MICRO_VERSION_REASONS = new Set<Doc<"smartViewVersions">["reason"]>([
  "block-content",
  "block-title",
  "block-prompt",
  "block-reorder",
]);

const getCondensedVersions = ({
  versions,
  currentVersionId,
  previewVersionId,
}: {
  versions: Doc<"smartViewVersions">[];
  currentVersionId: Id<"smartViewVersions"> | undefined;
  previewVersionId: Id<"smartViewVersions"> | null;
}): Doc<"smartViewVersions">[] => {
  const condensed = versions.filter(version => {
    if (version._id === currentVersionId || version._id === previewVersionId) {
      return true;
    }

    return isRestorePointVersion(version);
  });

  return condensed.length > 0 ? condensed : versions.slice(0, 1);
};

const isRestorePointVersion = (version: Doc<"smartViewVersions">): boolean => {
  if (!version.reason) {
    return true;
  }

  if (version.reason.startsWith("restore-v")) {
    return true;
  }

  return !MICRO_VERSION_REASONS.has(version.reason);
};

const labelForReason = (reason: Doc<"smartViewVersions">["reason"] | undefined): string => {
  if (!reason) return "Unknown change";
  if (reason === "create") return "Created";
  if (reason === "title-edit") return "Title edited";
  if (reason === "description-edit") return "Description edited";
  if (reason === "block-add") return "Block added";
  if (reason === "block-delete") return "Block deleted";
  if (reason === "block-reorder") return "Blocks reordered";
  if (reason === "block-content") return "Block content changed";
  if (reason === "block-title") return "Block title changed";
  if (reason === "recompose") return "Recomposed";
  if (reason.startsWith("restore-v")) return `Restored from ${reason.replace("restore-", "")}`;
  if (reason.startsWith("block-restore-")) return "Block restored";
  return reason;
};

const formatRelative = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const Panel = styled.aside`
  width: 320px;
  flex-shrink: 0;
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.surface.pane};
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing["4"]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const CondensedSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["2"]} ${({ theme }) => theme.spacing["3"]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const Row = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding: ${({ theme }) => theme.spacing["3"]} ${({ theme }) => theme.spacing["4"]};
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.background.button.onChrome.hover : "transparent"};
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: inherit;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  &:hover {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }
`;

const RowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RowSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
`;

const CurrentTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  padding: 1px 4px;
  border-radius: 3px;
`;
